import {
  SearchElementDateRange,
  SearchTaskFilter,
  SlaCurrentStatus,
  SlaEscalationStatus,
  TaskStatusType,
} from '@askporter/client-grieg-lyric';
import { EntityFilterNameLookup } from './useEntityFilterNames';
export interface ActiveTaskFilterChip {
  id: string;
  label: string;
  /**
   * true while this chip's proper label is still being resolved (a catalog fetch or entity name lookup in
   * flight) - callers should render a skeleton rather than the placeholder `label`, which is just the raw uid
   */
  isLoading?: boolean;
  /**
   * the filter patch to apply via setFilters to remove just this chip, leaving every other selection as-is
   */
  removePatch: Partial<SearchTaskFilter>;
}
interface CatalogEntry {
  uid: string;
  displayName: string;
}
type Translate = (key: string, options?: Record<string, string | number>) => string;
/**
 * One chip per distinct displayName across the currently-selected uids for a "grouped" catalog filter
 * (priority/status) - mirrors the sidebar's own checkboxes, which group multiple uids sharing a displayName
 * behind a single checkbox. Labels come straight from the raw catalog (task-filter-values), not the
 * count-filtered list the sidebar uses to decide what's offered - a selection stays represented even once it
 * no longer matches any results. While the catalog hasn't loaded yet, grouping isn't possible, so one
 * skeleton chip per selected uid stands in - once it loads, these are replaced by the properly-grouped chips
 * (which may combine some of them into one).
 */
const getGroupedCatalogChips = (
  filterKey: 'priority' | 'status',
  selectedUids: string[] | undefined,
  catalog: CatalogEntry[] | undefined,
  catalogsLoading: boolean,
): ActiveTaskFilterChip[] => {
  if (!selectedUids?.length) return [];
  if (!catalog?.length) {
    if (!catalogsLoading) return [];
    return selectedUids.map((uid) => ({
      id: `${filterKey}:${uid}`,
      label: uid,
      isLoading: true,
      removePatch: { [filterKey]: selectedUids.filter((selected) => selected !== uid) } as Partial<SearchTaskFilter>,
    }));
  }
  const uidsByDisplayName = new Map<string, string[]>();
  catalog.forEach(({ uid, displayName }) => {
    uidsByDisplayName.set(displayName, [...(uidsByDisplayName.get(displayName) ?? []), uid]);
  });
  const chips: ActiveTaskFilterChip[] = [];
  uidsByDisplayName.forEach((uids, displayName) => {
    if (!uids.some((uid) => selectedUids.includes(uid))) return;
    chips.push({
      id: `${filterKey}:${displayName.replace(/ /g, '_').toLowerCase()}`,
      label: displayName,
      removePatch: { [filterKey]: selectedUids.filter((uid) => !uids.includes(uid)) } as Partial<SearchTaskFilter>,
    });
  });
  return chips;
};
/**
 * One chip per selected uid for an "ungrouped" catalog filter (taskType) - each uid is its own distinct
 * option, so no displayName grouping is needed. Shows a skeleton for a uid the catalog hasn't resolved yet
 * while it's still loading; falls back to the raw uid once loaded if it's genuinely not found there.
 */
const getUngroupedCatalogChips = (
  filterKey: 'taskType',
  selectedUids: string[] | undefined,
  catalog: CatalogEntry[] | undefined,
  catalogsLoading: boolean,
): ActiveTaskFilterChip[] => {
  if (!selectedUids?.length) return [];
  return selectedUids.map((uid) => {
    const entry = catalog?.find((catalogEntry) => catalogEntry.uid === uid);
    return {
      id: `${filterKey}:${uid}`,
      label: entry?.displayName ?? uid,
      isLoading: !entry && catalogsLoading,
      removePatch: {
        [filterKey]: selectedUids.filter((selected) => selected !== uid),
      } as Partial<SearchTaskFilter>,
    };
  });
};
/**
 * One chip per selected uid for an entity-based filter (linked/assigned/escalated-to user, assigned/
 * escalated-to team, linked asset) - the label is a resolved display name (see useEntityFilterNames), shown
 * as a skeleton while it's still being fetched, falling back to the raw uid if it never resolves.
 */
const getEntityChips = (
  filterKey: 'linkedUser' | 'assignedUser' | 'escalatedToUser' | 'assignedTeam' | 'escalatedToTeam' | 'linkedAsset',
  selectedUids: string[] | undefined,
  entityNames: Record<string, EntityFilterNameLookup>,
): ActiveTaskFilterChip[] => {
  if (!selectedUids?.length) return [];
  return selectedUids.map((uid) => {
    const lookup = entityNames[uid];
    return {
      id: `${filterKey}:${uid}`,
      label: lookup?.name || uid,
      isLoading: !!lookup?.isLoading,
      removePatch: {
        [filterKey]: selectedUids.filter((selected) => selected !== uid),
      } as Partial<SearchTaskFilter>,
    };
  });
};
/**
 * One chip per selected value for a small, static enum filter (statusType, the SLA statuses, escalation
 * status) - labels are translated, mirroring the sidebar's own option lists. Always immediately available,
 * so never shown as a skeleton.
 */
const getEnumChips = <V extends string>(
  filterKey: string,
  selectedValues: V[] | undefined,
  labelFor: (value: V) => string,
): ActiveTaskFilterChip[] => {
  if (!selectedValues?.length) return [];
  return selectedValues.map((value) => ({
    id: `${filterKey}:${value}`,
    label: labelFor(value),
    removePatch: {
      [filterKey]: selectedValues.filter((selected) => selected !== value),
    } as Partial<SearchTaskFilter>,
  }));
};
/**
 * A single chip representing an applied date-range filter (created, or one of the SLA due/response/resolve
 * date ranges) - the section's own (always-available) title is used as the label rather than formatting the
 * specific dates. Removing it clears both bounds.
 */
const getDateRangeChip = (
  filterKey: string,
  range: SearchElementDateRange | undefined,
  label: string,
): ActiveTaskFilterChip[] => {
  if (!range?.before && !range?.after) return [];
  return [
    {
      id: `${filterKey}:date-range`,
      label,
      removePatch: { [filterKey]: { before: null, after: null } } as Partial<SearchTaskFilter>,
    },
  ];
};
export interface TaskFilterCatalogs {
  priorities?: CatalogEntry[];
  statuses?: CatalogEntry[];
  taskTypes?: CatalogEntry[];
}
/**
 * Builds the full set of active-filter chips for every task filter the sidebar exposes a control for.
 *
 * @param filters - the currently-applied SearchTaskFilter
 * @param catalogs - the raw (not count-filtered) priority/status/taskType catalogs from task-filter-values
 * @param catalogsLoading - true while task-filter-values is still loading - drives skeleton chips for
 * priority/status/taskType selections whose label can't be resolved yet
 * @param entityNames - uid -> resolved display name lookup for entity-based filters, see useEntityFilterNames
 * @param t - translation function, for the static-enum and date-range labels
 */
export const getActiveTaskFilterChips = (
  filters: SearchTaskFilter,
  catalogs: TaskFilterCatalogs,
  catalogsLoading: boolean,
  entityNames: Record<string, EntityFilterNameLookup>,
  t: Translate,
): ActiveTaskFilterChip[] => [
  ...getGroupedCatalogChips('priority', filters.priority, catalogs.priorities, catalogsLoading),
  ...getGroupedCatalogChips('status', filters.status, catalogs.statuses, catalogsLoading),
  ...getUngroupedCatalogChips('taskType', filters.taskType, catalogs.taskTypes, catalogsLoading),
  ...getEnumChips('statusType', filters.statusType, (value: TaskStatusType) =>
    t(`ns.task_filter:section:status_type:${value.toLowerCase()}`, { defaultValue: value }),
  ),
  ...getEnumChips('dueSlaStatus', filters.dueSlaStatus, (value: SlaCurrentStatus) =>
    t(`task_filter:section:sla:due_status:${value.toLowerCase()}`),
  ),
  ...getEnumChips('responseSlaStatus', filters.responseSlaStatus, (value: SlaCurrentStatus) =>
    t(`task_filter:section:sla:respond_status:${value.toLowerCase()}`),
  ),
  ...getEnumChips('resolutionSlaStatus', filters.resolutionSlaStatus, (value: SlaCurrentStatus) =>
    t(`task_filter:section:sla:resolve_status:${value.toLowerCase()}`),
  ),
  ...getEnumChips('escalationStatus', filters.escalationStatus, (value: SlaEscalationStatus) =>
    t(`task_filter:section:escalation_status:${value.toLowerCase()}`),
  ),
  ...getEntityChips('linkedUser', filters.linkedUser, entityNames),
  ...getEntityChips('assignedUser', filters.assignedUser, entityNames),
  ...getEntityChips('escalatedToUser', filters.escalatedToUser, entityNames),
  ...getEntityChips('assignedTeam', filters.assignedTeam, entityNames),
  ...getEntityChips('escalatedToTeam', filters.escalatedToTeam, entityNames),
  ...getEntityChips('linkedAsset', filters.linkedAsset, entityNames),
  ...getDateRangeChip('created', filters.created, t('ns.task_filter:section:created')),
  ...getDateRangeChip('resolutionDueDate', filters.resolutionDueDate, t('task_filter:section:sla:due_status:label')),
  ...getDateRangeChip(
    'responseDueDateTime',
    filters.responseDueDateTime,
    t('task_filter:section:sla:respond_status:label'),
  ),
  ...getDateRangeChip(
    'resolutionDueDateTime',
    filters.resolutionDueDateTime,
    t('task_filter:section:sla:resolve_status:label'),
  ),
];
